import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import { Injectable } from "@angular/core";
import { AbstractService } from "app/services/abstract.service";
import { Observable } from "rxjs";
import {
  IAPIListResult,
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "app/common/interfaces";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { ICreateVehicle, IUpdateVehicle } from "./interfaces/vehicle.interface";
import VehicleAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { StatusEnum } from '@albatrosdeveloper/ave-models-package';
import { ResponseVehicle, Vehicle } from "../../modals/dto/routes-detail-by-businesss-partnert-vehicle-dto";

const apiUrl = env.API_UNIT_MEASURE;
const moduleName = "vehicle";

@Injectable({
  providedIn: "root",
})
export class VechicleService extends AbstractService<
  VehicleAttributes,
  ICreateVehicle,
  IUpdateVehicle
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<VehicleAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene vehículos con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Cuatro objetos (OR lógico) - busca en 'code', 'name', 'type', 'plate'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param companyId Company filter
   * @param vehicleTypeId Vehicle type filter (array)
   * @param customerId Customer/Business Partner filter (array)
   * @param status Status filter (array)
   * @param isOwn Is own vehicle flag
   * @param isAll Flag to apply isOwn filter
   * @returns Paginated data
   *
   * Search fields: code, name, type, plate
   * Array filters: vehicleTypeId, customerId, status
   * Single filters: companyId, isOwn (conditional on isAll)
   */
  getAllPaginateVehicle(
    options: IPagination<VehicleAttributes>,
    companyId?,
    vehicleTypeId?,
    customerId?,
    status?,
    isOwn?,
    isAll?,
    /**
     * `true` suprime el overlay global de carga (`x-ave-ctx: skip-loading`).
     * Opt-in: las pantallas que listan vehículos dependen del overlay; la
     * sugerencia de optimización carga el catálogo con su propio estado.
     */
    silent: boolean = false
  ): Observable<IPaginationResult<VehicleAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, tipo vehículo, cliente, estados, propiedad.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<VehicleAttributes>[] => {
      const deletedFilter: FilterFn<VehicleAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        companyId
          ? andWhere('company.id' as keyof VehicleAttributes, Ops.eq(companyId))
          : noop(),
        vehicleTypeId && vehicleTypeId.length > 0
          ? andWhere('type.code' as keyof VehicleAttributes, Ops.in(...vehicleTypeId))
          : noop(),
        customerId && customerId.length > 0
          ? andWhere('businessPartner.id' as keyof VehicleAttributes, Ops.in(...customerId))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop(),
        isAll
          ? andWhere('isOwn', Ops.eq(isOwn))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<VehicleAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<VehicleAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por TYPE (solo si hay búsqueda)
    const searchByTypeQueryObject: FilterFn<VehicleAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (tercer objeto)
      andWhere("type", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 4: Condiciones base + búsqueda por PLATE (solo si hay búsqueda)
    const searchByPlateQueryObject: FilterFn<VehicleAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (cuarto objeto)
      andWhere("plate", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<VehicleAttributes>(
      // Expandimos los cuatro objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchByTypeQueryObject,
      ...searchByPlateQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<VehicleAttributes>>(
      API_URL,
      silent ? { headers: { 'x-ave-ctx': 'skip-loading' } } : {}
    );
  }

  findByFilter(
    filter: CombinedFilter<VehicleAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<VehicleAttributes>>(API_URL, {
      headers,
    });
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(
  ): Observable<IRequestResult<VehicleAttributes[]>> {
    const query = buildQuery<VehicleAttributes>(
      where("statusId", 1),
      select(["_id", "code", "name", "externalKeys", "plate", "businessPartner"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<VehicleAttributes[]>>(API_URL);
  }


  /**
  * Find to combo box
  * @returns Data
  */
  findToComboBoxTrip(
    options?: IPagination<VehicleAttributes>
  ): Observable<IRequestResult<VehicleAttributes[]>> {
    const query = buildQuery<VehicleAttributes>(
      where("statusId", 1),
      andWhere("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere("type", Ops.like(options.search ?? "", "i")),
      orWhere("plate", Ops.like(options.search ?? "", "i")),
      select(["_id", "code", "name", "externalKeys", "plate", "businessPartner"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/available?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<VehicleAttributes[]>>(API_URL);
  }

  /**
   * Vehículos disponibles para asignar a una sub-ruta (excluye los ya
   * asignados a una sub-ruta activa distinta de `excludeSubRouteId`).
   * @param excludeSubRouteId Sub-ruta a excluir de la validación de "ocupado" (modo edición)
   */
  findToComboBoxFilter(
    excludeSubRouteId?: string | null
  ): Observable<IRequestResult<VehicleAttributes[]>> {
    const API_URL: string = apiUrl + `api/${moduleName}/available-for-assignment`;
    const params = new URLSearchParams();
    if (excludeSubRouteId) {
      params.set('excludeSubRouteId', excludeSubRouteId);
    }
    const queryString = params.toString();
    return this.http.get<IRequestResult<VehicleAttributes[]>>(API_URL + (queryString ? `?${queryString}` : ''));
  }

  getWithTripsInfo(
    options: IPagination<any>,
    vehicleIds: string[]
  ): Observable<ResponseVehicle> {

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withTripsInfo?vehicleIds=${vehicleIds}`;
    return this.http.get<ResponseVehicle>(API_URL);
  }
}
